import { LanguagesEnum } from '@/api/enums';
import { ListingRequest, ListingResponse } from '@/api/types';
import { Account } from '@/client/accounts';
import { Branch } from '@/client/branches';
import {
  CampaignStatusesEnum,
  CampaignUserResultStatusesEnum,
} from '@/client/campaigns';
import {
  Course,
  CourseAttempt,
  CourseAttemptSummary,
  CourseEnrollmentSummary,
  CourseEntityScheduleEnum,
  UserStatusEnum,
} from '@/client/courses';
import { CustomRole } from '@/client/custom-roles';
import { Group } from '@/client/groups';
import { Role } from '@/client/roles';
import { UserOrigin } from './origins.enum';

export type ExternalUserMeta = {
  id: string;
};

export type UserMeta = {
  keycloakId?: string | null;
  affectedFromParent?: boolean;
  branch?: Branch;
  goPhishPosition?: string;
  isWelcomeEmailSent?: boolean;
  lastLogin?: Date;
  firstLogin?: Date;
  manuallyDeactivated?: boolean;
  manuallyDeactivatedByUser?: Pick<User, 'id' | 'username' | 'email' | 'name'>;
  manuallyDeactivatedOn?: Date;
  hubspotMeetingLink?: string;
  externalUser?: ExternalUserMeta;
  addUsersBannerDismissedAt?: Date;
};

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  name: string;
  email: string;
  account: Account;
  created: Date;
  updated: Date;
  active: boolean;
  role: Role;
  customRole?: CustomRole | null;
  scopeGroups?: Group[];
  groups: Group[];
  branch?: Branch | null;
  branches: Branch[];
  branchManagerOf: Branch[];
  accountManagerOf: Account[];
  meta?: UserMeta;
  ability?: {
    action: string;
    subject: string | string[];
  }[];
  featureFlagsAbility?: {
    action: string;
    subject: string | string[];
  }[];
  customRoleCeilingAbility?: {
    action: string;
    subject: string | string[];
  }[];
  canImpersonate?: boolean;
  accountsCount?: number;
  language?: string;
  enrolledCourses: Course[];
  scheduledCourses: Course[];
  attempts: CourseAttempt[];
  origin?: UserOrigin;
  accessState?: 'active' | 'pending' | null;
  assignedAccounts?: Account[];
  expiresOn?: Date;
  reactivatesOn?: Date;
  avatar?: string;
  contentLanguage?: LanguagesEnum;
  companyData?: UserCompanyData;
  domainVerified?: boolean;
  deletedAt?: string | null;
  gracePeriodDays?: number | null;
};

export type UserCompanyData = {
  jobTitle?: string;
  department?: string;
  companyName?: string;
  manager?: string;
  managerEmail?: string;
  managerUser?: User | null;
  country?: string;
  mobilePhone?: string;
  officeLocation?: string;
};

export type ListUserCoursesRequest = ListingRequest;
export type ListUserCoursesResponse = ListingResponse<UserCourses>;

export type UserCourses = {
  id: string;
  status: UserStatusEnum;
  enrollDate: Date;
  attempt: CourseAttemptSummary | null;
  course: Course;
  enrollment: CourseEnrollmentSummary | null;
  inheritedSchedule: {
    date: Date;
    source: CourseEntityScheduleEnum;
  } | null;
  schedule: {
    id: string;
    date: Date | null;
    executedAt: Date | null;
  } | null;
};

export type ListUserCampaignsRequest = ListingRequest;
export type ListUserCampaignsResponse = ListingResponse<UserCampaign>;

export type UserCampaign = {
  id: string;
  name: string;
  status: CampaignStatusesEnum;
  created: Date;
  updated: Date;
  launchDate: Date;
  sendByDate: Date;
  completeAfter: Date;
  completedDate: Date;
  type: UserCampaignType;
  lastResultStatus: CampaignUserResultStatusesEnum;
  emailsSent: number;
  emailsOpen: number;
  clickedLinks: number;
  submittedData: number;
  emailsReported: number;
};

export enum UserCampaignType {
  INDIVIDUAL = 'individual',
  COMPANY_WIDE = 'company-wide',
}

export type IntercomJwtResponse = {
  jwt: string;
};
